// src/components/AnalisisJobsPanel/AnalisisJobsPanel.jsx
// Panel flotante con los análisis en segundo plano del usuario: avance en
// vivo, PDF listo para descargar, reintentos y acceso para volver a verlos.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, IconButton, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import {
    ESTADOS_ACTIVOS,
    EVENTO_JOB_ACTUALIZADO,
    EVENTO_VENTANA_JOB,
    actualizarSesionJob,
    listarAnalisisJobs,
    marcarJobVisto,
    reintentarJob,
    urlDescargaReporte,
} from '../../utils/analisisJobs';

const ETIQUETAS = {
    en_cola: { texto: 'En cola', color: 'default' },
    procesando: { texto: 'Procesando', color: 'info' },
    visualizando: { texto: 'Generando PDF', color: 'info' },
    enviando: { texto: 'Enviando', color: 'info' },
    terminado: { texto: 'Listo', color: 'success' },
    error: { texto: 'Error', color: 'error' },
};

const UN_DIA_MS = 24 * 60 * 60 * 1000;

const esReciente = (job) => {
    const fecha = new Date(job.finalizadoEn || job.actualizadoEn || job.creadoEn).getTime();
    return Number.isFinite(fecha) && Date.now() - fecha < UN_DIA_MS;
};

const horaCorta = (fecha) => {
    try {
        return new Date(fecha).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
};

const AnalisisJobsPanel = ({ idUsuario, socket, socketSessionID, jobAbiertoId = null }) => {
    const [jobs, setJobs] = useState([]);
    const [abierto, setAbierto] = useState(true);
    const estadosPrevios = useRef({});

    const avisarCambio = useCallback((job) => {
        const previo = estadosPrevios.current[job.id];
        estadosPrevios.current[job.id] = job.estado;
        if (!previo || previo === job.estado) return;
        if (job.estado === 'terminado') {
            toast.success(job.correo?.enviado
                // Sin punto al final: se leía como parte del correo («...com.»).
                ? `${job.titulo}: listo. PDF enviado a: ${job.correo.destinatarios.join(', ')}`
                : `${job.titulo}: listo, el PDF ya está disponible.`, { autoClose: 8000 });
        } else if (job.estado === 'error') {
            toast.error(`${job.titulo}: ${job.error?.mensaje || job.mensaje || 'no se pudo completar.'}`, { autoClose: 10000 });
        }
    }, []);

    const fusionar = useCallback((job) => {
        // Los recálculos «solo capa» (líneas de pisoteo) los muestra el mapa.
        if (!job?.id || job.soloCapa) return;
        avisarCambio(job);
        setJobs((prev) => {
            const sinEste = prev.filter((j) => j.id !== job.id);
            return [job, ...sinEste].sort((a, b) => new Date(b.creadoEn) - new Date(a.creadoEn)).slice(0, 15);
        });
        setAbierto(true);
    }, [avisarCambio]);

    const cargar = useCallback(async () => {
        if (!idUsuario) return;
        try {
            const lista = await listarAnalisisJobs(idUsuario, { limite: 15 });
            lista.forEach((job) => { estadosPrevios.current[job.id] = job.estado; });
            setJobs(lista);
        } catch (error) {
            // Backend sin la ruta (aún sin desplegar): el panel no se muestra.
            setJobs([]);
        }
    }, [idUsuario]);

    useEffect(() => { cargar(); }, [cargar]);

    useEffect(() => {
        const onLanzado = (event) => fusionar(event.detail);
        window.addEventListener(EVENTO_VENTANA_JOB, onLanzado);
        return () => window.removeEventListener(EVENTO_VENTANA_JOB, onLanzado);
    }, [fusionar]);

    useEffect(() => {
        if (!socket) return undefined;
        socket.on(EVENTO_JOB_ACTUALIZADO, fusionar);
        // Al reconectar se pudo perder un aviso: se vuelve a leer la lista.
        socket.on('connect', cargar);
        return () => {
            socket.off(EVENTO_JOB_ACTUALIZADO, fusionar);
            socket.off('connect', cargar);
        };
    }, [socket, fusionar, cargar]);

    // Esta pestaña pasa a recibir en vivo los eventos de los análisis que
    // siguen corriendo (p. ej. tras recargar la página).
    const activosIds = useMemo(
        () => jobs.filter((j) => ESTADOS_ACTIVOS.includes(j.estado)).map((j) => j.id).join(','),
        [jobs]
    );
    useEffect(() => {
        if (!socketSessionID || !idUsuario || !activosIds) return;
        activosIds.split(',').forEach((id) => actualizarSesionJob(id, idUsuario, socketSessionID));
    }, [activosIds, socketSessionID, idUsuario]);

    const visibles = jobs.filter((job) => ESTADOS_ACTIVOS.includes(job.estado) || (!job.visto && esReciente(job)));
    if (visibles.length === 0) return null;
    const activos = visibles.filter((j) => ESTADOS_ACTIVOS.includes(j.estado)).length;

    const ocultar = async (job) => {
        setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, visto: true } : j)));
        await marcarJobVisto(job.id, idUsuario);
    };

    const reintentar = async (job) => {
        try {
            const actualizado = await reintentarJob(job.id, idUsuario);
            if (actualizado) fusionar(actualizado);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudo reintentar.');
        }
    };

    const ver = (job) => {
        window.location.assign(`/mapeo?analisisJob=${job.id}`);
    };

    return (
        <Paper
            elevation={6}
            sx={{
                position: 'fixed',
                right: 16,
                bottom: 16,
                width: 360,
                maxWidth: 'calc(100vw - 32px)',
                zIndex: 1300,
                borderRadius: 2,
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, bgcolor: 'var(--primary-color, #f97316)', color: '#fff', cursor: 'pointer' }}
                onClick={() => setAbierto((v) => !v)}
            >
                <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
                    Análisis en segundo plano{activos ? ` · ${activos} en curso` : ''}
                </Typography>
                {abierto ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
            </Box>
            {abierto && (
                <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
                    {visibles.map((job) => {
                        const etiqueta = ETIQUETAS[job.estado] || { texto: job.estado, color: 'default' };
                        const activo = ESTADOS_ACTIVOS.includes(job.estado);
                        const esEsteAnalisis = jobAbiertoId === job.id;
                        return (
                            <Box key={job.id} sx={{ px: 1.5, py: 1, borderBottom: '1px solid #eee' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }} noWrap title={job.titulo}>
                                        {job.titulo}
                                    </Typography>
                                    <Chip size="small" label={etiqueta.texto} color={etiqueta.color} />
                                    {!activo && (
                                        <Tooltip title="Ocultar">
                                            <IconButton size="small" onClick={() => ocultar(job)}>
                                                <CloseIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Typography variant="caption" color="text.secondary" component="div">
                                    {horaCorta(job.creadoEn)} · {job.estado === 'error' ? (job.error?.mensaje || job.mensaje) : job.mensaje}
                                </Typography>
                                {activo && (
                                    <LinearProgress
                                        variant={job.estado === 'en_cola' ? 'indeterminate' : 'determinate'}
                                        value={Math.max(3, Math.min(100, Number(job.progreso) || 0))}
                                        sx={{ mt: 0.75, borderRadius: 1 }}
                                    />
                                )}
                                {!activo && (
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                                        {job.reporte?.id && (
                                            <Button size="small" variant="outlined" href={urlDescargaReporte(job.reporte.id)}>
                                                Descargar PDF
                                            </Button>
                                        )}
                                        {(job.estado === 'terminado' || job.error?.fase !== 'gis') && !esEsteAnalisis && (
                                            <Button size="small" variant="text" onClick={() => ver(job)}>
                                                Ver análisis
                                            </Button>
                                        )}
                                        {job.estado === 'error' && job.error?.fase && job.error.fase !== 'gis' && (
                                            <Button size="small" variant="text" onClick={() => reintentar(job)}>
                                                {job.error.fase === 'correo' ? 'Reintentar envío' : 'Reintentar PDF'}
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Paper>
    );
};

export default AnalisisJobsPanel;
